import { getAdvancedStylingTextStyles } from '$lib/global/helper/advancedStylingText';
import { getBorderStyle, getBorderWidth } from '$lib/global/helper/border';
import { getBorderRadius } from '$lib/global/helper/border-radius';
import { getAdvancedStylingClasses } from '$lib/global/helper/getAdvancedStylingClasses';
import { getShadow } from '$lib/global/helper/shadow';

import type {
    BrandBorderRadius,
    BrandBorderStyle,
    BrandBorderWidth,
    BrandShadow,
    Theme,
} from '$lib/types/Theme';
import type { AdvancedStyling } from '$lib/types/Theming/AdvancedStyling';

export interface ResolvedAccordionItemStyling {
    // Inline colours.
    surfaceColor: string | undefined;
    borderColor: string | undefined;
    // Wrapper class tokens (applied via cn).
    borderRadiusClass: string | undefined;
    shadowClass: string | undefined;
    borderWidthClass: string | undefined;
    borderStyleClass: string | undefined;
    // Text styling for header (heading-mode) and body (body-mode).
    headingClassName: string;
    headingStyle: Record<string, string>;
    bodyClassName: string;
    bodyStyle: Record<string, string>;
    // textTransform applies to the body container only (heading mode excludes it per editor).
    textTransformClass: string | undefined;
}

interface Params {
    advancedStyling: AdvancedStyling | undefined;
    theme: Theme | undefined;
    // The per-item box.backgroundColor — takes precedence over theme.surface when non-transparent,
    // matching the React editor's surfaceColor resolution.
    boxBackgroundColor: string | undefined;
}

const isMeaningfulColor = (value: string | undefined): value is string =>
    !!value && value !== 'transparent';

// Combines per-item advancedStyling with theme fallbacks into the flat shape AccordionItem consumes.
// Priority: advancedStyling > theme > undefined (legacy). Colours `surface` and `borderColor` come
// from theme for surface/border (advancedStyling only overrides borderColor; surface is never in
// advancedStyling — matches editor).
export const resolveAccordionItemStyling = ({
    advancedStyling,
    theme,
    boxBackgroundColor,
}: Params): ResolvedAccordionItemStyling => {
    const advancedClasses = getAdvancedStylingClasses(advancedStyling, {
        shadowComponent: 'accordionItem',
        borderRadiusComponent: 'faqItem',
    });

    const themeShadowClass = theme?.shadow
        ? getShadow('accordionItem', theme.shadow as BrandShadow)
        : undefined;
    const themeBorderWidthClass = theme?.borderWidth
        ? getBorderWidth(theme.borderWidth as BrandBorderWidth)
        : undefined;
    const themeBorderStyleClass = theme?.borderStyle
        ? getBorderStyle(theme.borderStyle as BrandBorderStyle)
        : undefined;
    // Border-radius: if advancedStyling.borderRadius is not set but theme.borderRadius is, we still
    // want the theme's radius applied at scale C. If neither is set, `getBorderRadius` falls back
    // to the existing themeBorderRadius store — preserving legacy rendering.
    const borderRadiusClass =
        advancedClasses.borderRadius ??
        getBorderRadius('faqItem', theme?.borderRadius as BrandBorderRadius | undefined);

    const surfaceColor = isMeaningfulColor(boxBackgroundColor)
        ? boxBackgroundColor
        : theme?.surface;
    const borderColor = advancedStyling?.borderColor ?? theme?.border;

    const heading = getAdvancedStylingTextStyles(advancedStyling, { heading: true });
    const body = getAdvancedStylingTextStyles(advancedStyling);

    return {
        surfaceColor,
        borderColor,
        borderRadiusClass,
        shadowClass: advancedClasses.shadow ?? themeShadowClass,
        borderWidthClass: advancedClasses.borderWidth ?? themeBorderWidthClass,
        borderStyleClass: advancedClasses.borderStyle ?? themeBorderStyleClass,
        headingClassName: heading.className,
        headingStyle: heading.style,
        bodyClassName: body.className,
        bodyStyle: body.style,
        textTransformClass: advancedClasses.textTransform,
    };
};
